import React, { useState, useEffect } from "react";
import Axios from "../../_services/caller.service";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import styles from "./CalibrationConfig.module.scss";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../superAdmin/managePermissions/PermissionGate";
import { FiPause, FiPlay, FiClock, FiSend, FiCheckCircle } from "react-icons/fi";
import ManualReminders from "./ManualReminders";

const SLIDER_COLORS = {
  firstReminder: "#4CAF50",
  secondReminder: "#FFC107",
  thirdReminder: "#FF5722",
  overdueReminder: "#F44336",
};

const CalibrationConfig = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  // Scheduler state
  const [intervalDays, setIntervalDays] = useState(1);
  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(30);
  const [paused, setPaused] = useState(true);
  const [mode, setMode] = useState("manual");

  // Alerts state
  const [firstReminder, setFirstReminder] = useState(90);
  const [secondReminder, setSecondReminder] = useState(30);
  const [thirdReminder, setThirdReminder] = useState(7);
  const [overdueReminder, setOverdueReminder] = useState(-10);
  const [overdueMin, setOverdueMin] = useState(-365);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await Axios.get("/getConfig");
        const data = response.data.data;

        setIntervalDays(data.intervalDays ?? 1);
        setHour(data.time?.hour ?? 8);
        setMinute(data.time?.minute ?? 30);
        setPaused(data.paused ?? true);
        setMode(data.mode ?? "manual");

        setFirstReminder(data.firstReminder ?? 90);
        setSecondReminder(data.secondReminder ?? 30);
        setThirdReminder(data.thirdReminder ?? 7);
        setOverdueReminder(data.overdueReminder ?? -10);
        setOverdueMin(data.overdueReminderMin ?? -365);
      } catch {
        toast.error(t("calibration.error_loading_config"));
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // ── Toggle pause / resume ──
  const togglePause = async () => {
    try {
      const newPaused = !paused;
      const response = await Axios.post("/reloadIntervalReminders", {
        intervalDays,
        hour,
        minute,
        paused: newPaused,
      });
      setPaused(newPaused);
      toast.success(response.data.message);
    } catch {
      toast.error(t("calibration.error_changing_state"));
    }
  };

  // ── Bascule mode automatique (CRON) / manuel ──
  const toggleMode = async (nextMode) => {
    try {
      const response = await Axios.post("/reloadIntervalReminders", {
        intervalDays,
        hour,
        minute,
        paused,
        mode: nextMode,
      });
      setMode(nextMode);
      toast.success(response.data.message);
    } catch {
      toast.error(t("calibration.error_changing_state"));
    }
  };

  // ── Save scheduler (interval + heure) ──
  const saveScheduler = async (e) => {
    e.preventDefault();
    try {
      const response = await Axios.post("/reloadIntervalReminders", {
        intervalDays,
        hour,
        minute,
        paused,
      });
      toast.success(response.data.message);
    } catch {
      toast.error(t("calibration.error_updating_schedule"));
    }
  };

  // ── Save alerts config ──
  const saveAlerts = async () => {
    try {
      await Axios.post("/updateConfig", {
        firstReminder,
        secondReminder,
        thirdReminder,
        overdueReminder,
        overdueReminderMin: overdueMin,
      });
      toast.success(t("calibration.thresholds_saved"));
    } catch {
      toast.error(t("calibration.error_saving_thresholds"));
    }
  };

  // ── Slider constraints ──
  const handleSlider = (key, value) => {
    if (key === "firstReminder") {
      setFirstReminder(value);
      if (value < secondReminder) {
        setSecondReminder(value);
        if (value < thirdReminder) setThirdReminder(value);
      }
    } else if (key === "secondReminder") {
      setSecondReminder(value);
      if (value > firstReminder) setFirstReminder(value);
      if (value < thirdReminder) setThirdReminder(value);
    } else if (key === "thirdReminder") {
      setThirdReminder(value);
      if (value > secondReminder) setSecondReminder(value);
    } else if (key === "overdueReminder") {
      setOverdueReminder(value);
    }
  };

  const handleOverdueMin = (value) => {
    setOverdueMin(value);
    if (overdueReminder < value) setOverdueReminder(value);
  };

  if (loading) return <p className={styles.loading}>{t("common.loading")}</p>;

  const isManual = mode === "manual";

  return (
    <div className={styles.container}>
      {/* ═══════════════ SÉLECTEUR DE MODE ═══════════════ */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t("calibration.mode_section_title")}</h3>
        <p className={styles.sectionHint}>{t("calibration.mode_section_hint")}</p>

        <PermissionGate
          actionKey="manageCertificat.configScheduler.pause"
        >
          <div className={styles.modeCards}>
            <button
              type="button"
              className={`${styles.modeCard} ${!isManual ? styles.modeCardActiveAuto : ""}`}
              onClick={() => toggleMode("auto")}
              aria-pressed={!isManual}
            >
              <span className={styles.modeCardIcon}><FiClock /></span>
              <span className={styles.modeCardBody}>
                <span className={styles.modeCardTitle}>
                  {t("calibration.mode_auto")}
                  {!isManual && <span className={styles.modeActiveTag}><FiCheckCircle /> {t("calibration.mode_active")}</span>}
                </span>
                <span className={styles.modeCardDesc}>{t("calibration.mode_auto_banner")}</span>
              </span>
            </button>

            <button
              type="button"
              className={`${styles.modeCard} ${isManual ? styles.modeCardActiveManual : ""}`}
              onClick={() => toggleMode("manual")}
              aria-pressed={isManual}
            >
              <span className={styles.modeCardIcon}><FiSend /></span>
              <span className={styles.modeCardBody}>
                <span className={styles.modeCardTitle}>
                  {t("calibration.mode_manual")}
                  {isManual && <span className={styles.modeActiveTag}><FiCheckCircle /> {t("calibration.mode_active")}</span>}
                </span>
                <span className={styles.modeCardDesc}>{t("calibration.mode_manual_banner")}</span>
              </span>
            </button>
          </div>
        </PermissionGate>
      </section>

      {/* ═══════════════ MODE MANUEL : panneau d'envoi ═══════════════ */}
      {isManual && <ManualReminders />}

      {/* ═══════════════ MODE AUTO : statut + planification ═══════════════ */}
      {!isManual && (
      <>
      {/* ═══════════════ STATUS BANNER ═══════════════ */}
      <div className={`${styles.statusBanner} ${paused ? styles.statusPaused : styles.statusActive}`}>
        <div className={styles.statusLeft}>
          <span className={`${styles.statusDot} ${paused ? styles.dotPaused : styles.dotActive}`} />
          <div>
            <strong>{paused ? t("calibration.service_paused") : t("calibration.service_active")}</strong>
            <span className={styles.statusDesc}>
              {paused
                ? t("calibration.paused_message")
                : t("calibration.send_schedule", { days: intervalDays, time: `${String(hour).padStart(2, "0")}h${String(minute).padStart(2, "0")}` })}
            </span>
          </div>
        </div>
        <PermissionGate actionKey="manageCertificat.configScheduler.pause">
          <button onClick={togglePause} className={`${styles.toggleBtn} ${paused ? styles.toggleResume : styles.togglePause}`}>
            {paused ? <><FiPlay /> {t("common.activate")}</> : <><FiPause /> {t("common.pause")}</>}
          </button>
        </PermissionGate>
      </div>

      {/* ═══════════════ SCHEDULER ═══════════════ */}
      {!paused && (
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t("calibration.schedule_title")}</h3>
        <p className={styles.sectionHint}>
          {t("calibration.schedule_description")}
        </p>

        <form onSubmit={saveScheduler} className={styles.schedulerForm}>
          <div className={styles.field}>
            <label htmlFor="intervalDays">{t("calibration.frequency_days")}</label>
            <input
              id="intervalDays"
              type="number"
              min="1"
              value={intervalDays}
              onChange={(e) => setIntervalDays(parseInt(e.target.value, 10) || 1)}
            />
            <span className={styles.fieldHint}>{t("calibration.frequency_hint")}</span>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label htmlFor="hour">{t("common.hour")}</label>
              <input
                id="hour"
                type="number"
                min="0"
                max="23"
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <span className={styles.timeSep}>:</span>
            <div className={styles.field}>
              <label htmlFor="minute">{t("common.minute")}</label>
              <input
                id="minute"
                type="number"
                min="0"
                max="59"
                value={minute}
                onChange={(e) => setMinute(parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>

          <PermissionGate actionKey="manageCertificat.configScheduler.updtate">
            <button type="submit" className={styles.btnSave}>
              {t("calibration.save_schedule")}
            </button>
          </PermissionGate>
        </form>
      </section>
      )}
      </>
      )}

      {/* ═══════════════ SEUILS DE RELANCE ═══════════════ */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t("calibration.thresholds_title")}</h3>
        <p className={styles.sectionHint}>
          {t("calibration.thresholds_hint")}
        </p>

        <div className={styles.slidersGrid}>
          {/* 1re relance */}
          <div className={styles.sliderCard}>
            <div className={styles.sliderHeader}>
              <span className={styles.sliderDot} style={{ background: SLIDER_COLORS.firstReminder }} />
              <span>1<sup>re</sup> {t("calibration.reminders").toLowerCase()}</span>
            </div>
            <Slider
              min={0} max={365} step={1}
              value={firstReminder}
              onChange={(v) => handleSlider("firstReminder", v)}
              trackStyle={{ backgroundColor: SLIDER_COLORS.firstReminder }}
              handleStyle={{ borderColor: SLIDER_COLORS.firstReminder }}
              railStyle={{ backgroundColor: "#E0E0E0" }}
            />
            <span className={styles.sliderValue} style={{ color: SLIDER_COLORS.firstReminder }}>
              {t("calibration.days_before", { days: firstReminder })}
            </span>
          </div>

          {/* 2e relance */}
          <div className={styles.sliderCard}>
            <div className={styles.sliderHeader}>
              <span className={styles.sliderDot} style={{ background: SLIDER_COLORS.secondReminder }} />
              <span>2<sup>e</sup> {t("calibration.reminders").toLowerCase()}</span>
            </div>
            <Slider
              min={0} max={firstReminder} step={1}
              value={secondReminder}
              onChange={(v) => handleSlider("secondReminder", v)}
              trackStyle={{ backgroundColor: SLIDER_COLORS.secondReminder }}
              handleStyle={{ borderColor: SLIDER_COLORS.secondReminder }}
              railStyle={{ backgroundColor: "#E0E0E0" }}
            />
            <span className={styles.sliderValue} style={{ color: SLIDER_COLORS.secondReminder }}>
              {t("calibration.days_before", { days: secondReminder })}
            </span>
          </div>

          {/* 3e relance */}
          <div className={styles.sliderCard}>
            <div className={styles.sliderHeader}>
              <span className={styles.sliderDot} style={{ background: SLIDER_COLORS.thirdReminder }} />
              <span>3<sup>e</sup> {t("calibration.reminders").toLowerCase()}</span>
            </div>
            <Slider
              min={0} max={secondReminder} step={1}
              value={thirdReminder}
              onChange={(v) => handleSlider("thirdReminder", v)}
              trackStyle={{ backgroundColor: SLIDER_COLORS.thirdReminder }}
              handleStyle={{ borderColor: SLIDER_COLORS.thirdReminder }}
              railStyle={{ backgroundColor: "#E0E0E0" }}
            />
            <span className={styles.sliderValue} style={{ color: SLIDER_COLORS.thirdReminder }}>
              {t("calibration.days_before", { days: thirdReminder })}
            </span>
          </div>

          {/* Retard */}
          <div className={styles.sliderCard}>
            <div className={styles.sliderHeader}>
              <span className={styles.sliderDot} style={{ background: SLIDER_COLORS.overdueReminder }} />
              <span>{t("calibration.overdue_alert")}</span>
            </div>
            <Slider
              min={overdueMin} max={0} step={1}
              value={overdueReminder}
              onChange={(v) => handleSlider("overdueReminder", v)}
              trackStyle={{ backgroundColor: SLIDER_COLORS.overdueReminder }}
              handleStyle={{ borderColor: SLIDER_COLORS.overdueReminder }}
              railStyle={{ backgroundColor: "#E0E0E0" }}
            />
            <span className={styles.sliderValue} style={{ color: SLIDER_COLORS.overdueReminder }}>
              {t("calibration.days_after_deadline", { days: Math.abs(overdueReminder) })}
            </span>
          </div>

          {/* Suppression auto */}
          <div className={styles.sliderCard}>
            <div className={styles.sliderHeader}>
              <span className={styles.sliderDot} style={{ background: "#9E9E9E" }} />
              <span>{t("calibration.auto_removal")}</span>
            </div>
            <Slider
              min={-365} max={overdueReminder} step={1}
              value={overdueMin}
              onChange={handleOverdueMin}
              trackStyle={{ backgroundColor: "#9E9E9E" }}
              handleStyle={{ borderColor: "#9E9E9E" }}
              railStyle={{ backgroundColor: "#E0E0E0" }}
            />
            <span className={styles.sliderValue} style={{ color: "#666" }}>
              {t("calibration.deletion_message", { days: Math.abs(overdueMin) })}
            </span>
          </div>
        </div>

        <PermissionGate actionKey="manageCertificat.remindersInterval.update">
          <button className={styles.btnSave} onClick={saveAlerts} type="button">
            {t("calibration.save_thresholds")}
          </button>
        </PermissionGate>
      </section>
    </div>
  );
};

export default CalibrationConfig;
